"use client";

import { CatItem } from "@/types/cat";
import { RarityBadge } from "@/components/shared/RarityBadge";
import { OpenSeaLink } from "@/components/shared/OpenSeaLink";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

interface CatDetailModalProps {
  cat: CatItem | null;
  onClose: () => void;
}

export function CatDetailModal({ cat, onClose }: CatDetailModalProps) {
  if (!cat) return null;

  return (
    <Dialog open={!!cat} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl border-border bg-card p-0">
        <DialogTitle className="sr-only">{cat.name} Details</DialogTitle>
        <div className="grid md:grid-cols-2">
          <div className="relative aspect-square">
            <img
              src={cat.image}
              alt={cat.name}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-pixel text-lg">{cat.name}</h3>
                <div className="mt-3">
                  <RarityBadge rarity={cat.rarity} />
                </div>
              </div>
              <OpenSeaLink tokenId={cat.id} className="shrink-0" />
            </div>

            <p className="mt-4 text-sm text-muted-foreground">
              {cat.description}
            </p>

            {cat.imageMissing && (
              <div className="mt-4 rounded-lg bg-amber-500/10 p-3 text-sm text-amber-300">
                This cat image is still being generated; currently showing a rarity preview.
              </div>
            )}

            <div className="mt-6 space-y-3">
              <h4 className="font-semibold">Traits</h4>
              {Object.entries(cat.traits).map(([key, value]) => (
                <div
                  key={key}
                  className="flex items-center justify-between border-b border-border pb-2 text-sm"
                >
                  <span className="text-muted-foreground">{key}</span>
                  <span className="font-medium">{value}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-3">
              <h4 className="font-semibold">Stats</h4>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Luck", value: cat.stats.luck },
                  { label: "Charm", value: cat.stats.charm },
                  { label: "Power", value: cat.stats.power },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-lg border border-border bg-background p-3 text-center"
                  >
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                    <p className="text-lg font-bold text-primary">{stat.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
